import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import TrafficOverview from "@/components/TrafficOverview";
import { RangeBar, daysBetween, shortDay, useRange } from "@/pages/developers/console/range";

export default function AiApps() {
  const qc = useQueryClient();
  const [rateDraft, setRateDraft] = useState(null);
  const range = useRange();
  const { tz, startDate, endDate } = range;

  const { data: graphSettings } = useQuery({
    queryKey: ["brand-graph"],
    queryFn: async () => (await api.get("/brand/graph")).data,
  });
  const { data: publishers = [] } = useQuery({
    queryKey: ["brand-publishers"],
    queryFn: async () => (await api.get("/brand/publishers")).data,
  });
  const { data: appearances } = useQuery({
    queryKey: ["brand-appearances"],
    queryFn: async () => (await api.get("/brand/appearances")).data,
  });
  const { data: brandStats } = useQuery({
    queryKey: ["brand-stats", startDate, endDate, tz],
    queryFn: async () => (await api.get("/brand/stats", { params: range.params })).data,
  });

  const saveGraph = async (patch) => {
    try {
      await api.put("/brand/graph", patch);
      qc.invalidateQueries({ queryKey: ["brand-graph"] });
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setPublisher = async (id, action) => {
    try {
      await api.post(`/brand/publishers/${id}/${action}`);
      qc.invalidateQueries({ queryKey: ["brand-publishers"] });
      toast.success(action === "allow" ? "App allowed" : "App blocked");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rate = rateDraft ?? Math.round((graphSettings?.commission_rate ?? 0.08) * 100);

  return (
    <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Seller studio</div>
      <h1 className="font-serif text-4xl tracking-tight mb-3">AI shopping apps</h1>
      <p className="text-sm text-aura-muted max-w-xl mb-10">
        Shopping assistants and agents built on Shoptosa can recommend your listings. Choose whether they see you,
        what commission they earn on a sale, and which apps are allowed.
      </p>

      <div className="border border-aura-border p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Show my listings to AI apps</div>
          <div className="text-xs text-aura-muted mt-1">Turn off to hide every listing from them.</div>
        </div>
        <button
          onClick={() => saveGraph({ published: !graphSettings?.published })}
          className={`px-4 py-2 text-xs rounded-full ${graphSettings?.published ? "btn-accent" : "border border-aura-border"}`}
          data-testid="toggle-published"
        >
          {graphSettings?.published ? "On" : "Off"}
        </button>
      </div>

      <div className="border border-aura-border p-5 mb-6">
        <div className="text-sm font-medium mb-2">Commission for apps</div>
        <p className="text-xs text-aura-muted mb-3">Paid on sales an app sends to you. Default 8%.</p>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min="0"
            max="25"
            step="1"
            value={rate}
            onChange={(e) => setRateDraft(Number(e.target.value))}
            onMouseUp={(e) => {
              const v = Number(e.currentTarget.value);
              setRateDraft(null);
              saveGraph({ commission_rate: v / 100 });
            }}
            onTouchEnd={(e) => {
              const v = Number(e.currentTarget.value);
              setRateDraft(null);
              saveGraph({ commission_rate: v / 100 });
            }}
            className="flex-1"
            data-testid="commission-rate"
          />
          <span className="font-mono text-sm w-12">{rate}%</span>
        </div>
      </div>

      <div className="border border-aura-border p-5 mb-6 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium">Who can show you</div>
          <div className="text-xs text-aura-muted mt-1">Every app, or only the ones you allow below.</div>
        </div>
        <button
          onClick={() => saveGraph({ visibility: graphSettings?.visibility === "allowlist" ? "all" : "allowlist" })}
          className="px-4 py-2 text-xs border border-aura-border rounded-full"
          data-testid="toggle-visibility"
        >
          {graphSettings?.visibility === "allowlist" ? "Allowed apps only" : "Every app"}
        </button>
      </div>

      <div className="mb-10">
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">Apps</div>
        <div className="border border-aura-border divide-y divide-aura-border">
          {publishers.length === 0 && <div className="px-5 py-6 text-sm text-aura-muted">No apps yet.</div>}
          {publishers.map((p) => (
            <div key={p.id} className="flex items-center justify-between px-5 py-3">
              <div>
                <div className="text-sm">{p.name}</div>
                <div className="text-[11px] text-aura-muted uppercase tracking-luxe">{p.status}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setPublisher(p.id, "allow")} className="text-xs text-aura-muted hover:text-aura-ink" data-testid={`allow-${p.id}`}>Allow</button>
                <button onClick={() => setPublisher(p.id, "block")} className="text-xs text-aura-muted hover:text-aura-accent" data-testid={`block-${p.id}`}>Block</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <RangeBar range={range} />
      <TrafficOverview
        totals={brandStats?.totals}
        allTime={brandStats?.all_time || { estimated_commission: brandStats?.estimated_commission }}
        series={brandStats?.series || []}
        activity={brandStats?.activity || []}
        rangeLabel={endDate === range.today ? `last ${daysBetween(startDate, endDate)}d` : `${shortDay(startDate)} – ${shortDay(endDate)}`}
        tz={tz}
      />

      <div>
        <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-3">What shoppers asked</div>
        {(appearances?.appearances || []).length === 0 ? (
          <p className="text-sm text-aura-muted">Your listings have not appeared in an app yet.</p>
        ) : (
          <div className="border border-aura-border divide-y divide-aura-border">
            {appearances.appearances.slice(0, 12).map((a, i) => (
              <div key={i} className="px-5 py-3 text-sm">
                <div>{a.query || a.type}</div>
                <div className="text-[11px] text-aura-muted mt-1">
                  {a.publisher || "App"} · {a.type}{a.rank ? ` · #${a.rank}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
